import React, { useMemo, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { BottomNavigation, BottomNavigationAction, Paper } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PersonIcon from '@mui/icons-material/Person';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import { useAuth } from '../../../context/AuthContext';
import { isValidUser } from '../../../Utils/validate';
import { CheckoutContext, CheckoutContextProps } from '../../../context/CheckoutContext';
import { rememberPostAuthReturn } from '../../../Utils/postAuthReturn';
import shakaIcon from '../../../assets/icons/Shaka Icon.png';

interface NavigationItem {
  label: string;
  basePath: string;
  icon: React.ReactNode;
  requiresAuth?: boolean;
}

const baseNavigationItems: NavigationItem[] = [
  { label: 'Home', basePath: '/', icon: <HomeIcon /> },
  { label: 'Events', basePath: '/MarketplaceClasses', icon: <CalendarMonthIcon /> },
  { label: 'Sports', basePath: '/MarketplaceCalendar', icon: <img src={shakaIcon} alt="Sports" width="32" height="22" style={{ objectFit: 'contain', display: 'block', margin: '0 auto', verticalAlign: 'middle' }} /> },
  { label: 'Packages', basePath: '/packages', icon: <ShoppingBagIcon /> },
  { label: 'Profile', basePath: '/My-profile', icon: <PersonIcon />, requiresAuth: true },
];

const MobileBottomNavigation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { company, selectedSession } = useContext(CheckoutContext) as CheckoutContextProps;

  // Generate navigation items with companyId - always show all items
  const navigationItems = useMemo(() => {
    const companyId = company?.id;
    return baseNavigationItems.map(item => ({
      ...item,
      path: companyId ? `${item.basePath}?companyId=${companyId}` : item.basePath,
    }));
  }, [company?.id]);

  // Find the current active index based on the pathname
  const currentIndex = navigationItems.findIndex(item => {
    const currentPath = location.pathname.toLowerCase();
    const basePath = item.basePath.toLowerCase();
    
    // Special case: Profile should be active on Register page too
    if (item.requiresAuth && currentPath.startsWith('/register')) {
      return true;
    }
    
    // Exact match for home, prefix match for others
    return basePath === '/' ? currentPath === '/' : currentPath.startsWith(basePath);
  });

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    const selectedItem = navigationItems[newValue];
    
    // If profile item is clicked and user is not authenticated, navigate to register
    if (selectedItem.requiresAuth && !isValidUser(user)) {
      const companyId = company?.id;
      rememberPostAuthReturn(selectedSession);
      navigate(companyId ? `/Register?companyId=${companyId}` : '/Register');
    } else {
      navigate(selectedItem.path);
    }
  };

  return (
    <Paper
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        display: { xs: 'block', md: 'none' }, // Only show on mobile
        boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.1)',
      }}
      elevation={3}
    >
      <BottomNavigation
        value={currentIndex !== -1 ? currentIndex : 0}
        onChange={handleChange}
        showLabels
        sx={{
          height: '70px',
          '& .MuiBottomNavigationAction-root': {
            minWidth: 'auto',
            padding: '6px 0'
          },
          '& .MuiBottomNavigationAction-label': {
            fontSize: '0.75rem',
            marginTop: '4px',
          },
          '& .MuiBottomNavigationAction-label.Mui-selected': {
            fontSize: '0.75rem',
          },
        }}
      >
        {navigationItems.map((item, index) => (
          <BottomNavigationAction
            key={index}
            label={item.label}
            icon={item.icon}
          />
        ))}
      </BottomNavigation>
    </Paper>
  );
};

export default MobileBottomNavigation;

